import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, NavigateFunction } from "react-router-dom";
import {
  Text,
  Loader,
  Divider,
  Space,
  Table,
  Badge,
  Paper,
  Group,
  Button,
  ScrollArea,
  Alert,
  ActionIcon,
} from "@mantine/core";
import {
  GasStation,
  Route,
  ClipboardData,
  MapSearch,
  ArrowRight,
} from "tabler-icons-react";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  Polyline,
  useLoadScript,
} from "@react-google-maps/api";
import { IconSparkles } from "@tabler/icons-react";

import { auth } from "../firebase";
import VerifiedIcon from "../components/listings/verified-icon";

const API_BASE = process.env.REACT_APP_APP_ENV === "local"
  ? "http://127.0.0.1:5001/twodata-dev/europe-west3/tenantapi"
  : "https://europe-west3-twodata-dev.cloudfunctions.net/tenantapi";
const X_API_KEY = process.env.REACT_APP_X_API_KEY || "0f247291-b4bb-4895-91ac-ba26af7e55a2";

// --- Types ---

export type DateInput = string | number | Date | { seconds?: number; _seconds?: number } | null | undefined;

export interface FuelOverviewItem {
  id: string;
  _collection: string;
  _typeLabel: string;
  transactionDate: DateInput;
  driverOperator?: string;
  driverNickName?: string;
  fuelReceiverSign?: string;
  supplierDriverName?: string;
  signedBy?: string;
  documentNumber?: string;
  fleetDisplayName?: string;
  mobileTankName?: string;
  tankName?: string;
  gpsLocation?: number[] | GpsCoords | string | null;
  location?: number[] | GpsCoords | string | null;
  [key: string]: unknown;
}

export interface FlexOverviewItem {
  id: string;
  templateId?: string;
  _templateName?: string;
  submissionDate: DateInput;
  submitterName?: string;
  submitterEmail?: string;
  documentNumber?: string;
  formData?: Record<string, unknown>;
  data?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface TripOverviewItem {
  id: string;
  description?: string;
  status?: string;
  pickupAddress?: string;
  fleetDisplayName?: string;
  fleetOperatorNameAndSurname?: string;
  locationSnapshots?: (GpsCoords & { lat?: number | string; lon?: number | string })[];
  capturedTripDepartData?: { submissionDate?: DateInput; date?: DateInput; capturedDate?: DateInput; notes?: string; delayReason?: string; reason?: string }[];
  capturedTripCheckPointData?: { submissionDate?: DateInput; date?: DateInput; capturedDate?: DateInput; notes?: string; delayReason?: string; reason?: string }[];
  capturedTripDelayData?: { submissionDate?: DateInput; date?: DateInput; capturedDate?: DateInput; notes?: string; delayReason?: string; reason?: string }[];
  capturedTripArriveData?: { submissionDate?: DateInput; date?: DateInput; capturedDate?: DateInput; notes?: string; delayReason?: string; reason?: string }[];
  capturedTripCompleteData?: { submissionDate?: DateInput; date?: DateInput; capturedDate?: DateInput; notes?: string; delayReason?: string; reason?: string }[];
  [key: string]: unknown;
}

export interface GpsCoords {
  lat?: number | string | null;
  latitude?: number | string | null;
  _latitude?: number | string | null;
  lon?: number | string | null;
  lng?: number | string | null;
  longitude?: number | string | null;
  _longitude?: number | string | null;
}

export interface GpsItem {
  gpsLocation?: number[] | GpsCoords | string | null;
  location?: number[] | GpsCoords | string | null;
  locationSnapshots?: (GpsCoords & { lat?: number | string; lon?: number | string })[];
  formData?: Record<string, unknown>;
  data?: Record<string, unknown>;
}

// --- Helpers ---

function toDateString(val: DateInput): string {
  if (!val) return "—";
  let ms: number;
  if (typeof val === "object" && val !== null && ("seconds" in val || "_seconds" in val)) {
    ms = (val.seconds || val._seconds || 0) * 1000;
  } else {
    ms = new Date(val as string | number | Date).getTime();
  }
  if (isNaN(ms)) return "—";
  const d = new Date(ms);
  return (
    d.toLocaleDateString("en-GB") + " " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
  );
}

function toIsoDateOnly(val: DateInput): string {
  if (!val) return "";
  let ms: number;
  if (typeof val === "object" && val !== null && ("seconds" in val || "_seconds" in val)) {
    ms = (val.seconds || val._seconds || 0) * 1000;
  } else {
    ms = new Date(val as string | number | Date).getTime();
  }
  if (isNaN(ms)) return "";
  const d = new Date(ms);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function extractGps(item: GpsItem): { lat: number; lng: number } | null {
  const parseVal = (v: number[] | GpsCoords | string | null | undefined): { lat: number; lng: number } | null => {
    if (!v) return null;

    let coords: { lat: number; lng: number } | null = null;

    // Handle Array format [lng, lat]
    if (Array.isArray(v) && v.length === 2) {
      const val1 = Number(v[0]);
      const val2 = Number(v[1]);
      if (!isNaN(val1) && !isNaN(val2)) {
        if (Math.abs(val1) > Math.abs(val2)) {
          coords = { lat: val2, lng: val1 }; // [lng, lat]
        } else {
          coords = { lat: val1, lng: val2 }; // [lat, lng]
        }
      }
    }

    // Handle Object format
    else if (typeof v === "object" && !Array.isArray(v)) {
      const rawLat = v.lat ?? v.latitude ?? v._latitude;
      const rawLng = v.lon ?? v.lng ?? v.longitude ?? v._longitude;
      const lat = Number(rawLat);
      const lng = Number(rawLng);
      if (rawLat !== null && rawLat !== undefined && String(rawLat) !== "" &&
          rawLng !== null && rawLng !== undefined && String(rawLng) !== "" &&
          !isNaN(lat) && !isNaN(lng)) {
        coords = { lat, lng };
      }
    }

    // Handle String format "lat,lng"
    else if (typeof v === "string" && v.includes(",")) {
      const [lat, lng] = v.split(",").map(Number);
      if (!isNaN(lat) && !isNaN(lng)) {
        coords = { lat, lng };
      }
    }

    if (coords && coords.lat === 0 && coords.lng === 0) {
      return null;
    }

    return coords;
  };

  // 1. Fuel transactions: gpsLocation or location field
  const fuelRes = parseVal(item.gpsLocation || item.location);
  if (fuelRes) return fuelRes;

  // 2. Trips: first locationSnapshot
  if (item.locationSnapshots && item.locationSnapshots.length > 0) {
    const tripRes = parseVal(item.locationSnapshots[0]);
    if (tripRes) return tripRes;
  }

  // 3. Flex: check for gps-type fields in formData or data
  const dataObj = item.formData || item.data;
  if (dataObj && typeof dataObj === "object") {
    for (const key of Object.keys(dataObj)) {
      const field = dataObj[key];
      let v = field;
      // Handle nested value structure
      if (field && typeof field === "object" && "value" in field) {
        v = (field as { value: unknown }).value;
      }
      const flexRes = parseVal(v as number[] | GpsCoords | string | null | undefined);
      if (flexRes) return flexRes;
    }
  }

  return null;
}

// Collection → route mapping for click-through
const FUEL_ROUTE_MAP = {
  capturedRefuel: "/dashboard/capturedRefuel",
  capturedFuelTankTransfer: "/dashboard/capturedFuelTankTransfer",
  capturedFuelTankIssue: "/dashboard/capturedFuelTankIssue",
  capturedBulkFuelReceived: "/dashboard/capturedBulkFuelReceived",
  capturedTankLevelReading: "/dashboard/capturedTankLevelReading",
};

const FUEL_TYPE_COLORS = {
  "Roadside refuel": "red",
  "Fuel transfer": "cap-blue",
  "Onsite refuel": "cap-purple",
  "Bulk delivery": "yellow",
  "Tank reading": "cap-navy",
};

interface SectionHeaderProps {
  icon: React.ReactNode;
  title: string;
  color?: string;
  actionLabel?: string;
  onAction?: () => void;
}

function SectionHeader({ icon, title, color, actionLabel, onAction }: SectionHeaderProps) {
  return (
    <Group position="apart" align="center">
      <Group spacing="xs">
        {icon}
        <Text size="lg" weight={400} style={{ color }}>
          {title}
        </Text>
      </Group>
      {actionLabel && (
        <Button
          variant="outline"
          size="sm"
          color="cap-blue"
          rightIcon={<ArrowRight size={14} />}
          onClick={onAction}
        >
          {actionLabel}
        </Button>
      )}
    </Group>
  );
}

export function FuelTable({ items, navigate }: { items: FuelOverviewItem[]; navigate: NavigateFunction }) {
  if (!items?.length) {
    return (
      <Text size="sm" color="dimmed" py="md" align="center">
        No recent fuel transactions
      </Text>
    );
  }
  return (
    <ScrollArea mt="xs">
      <Table striped highlightOnHover style={{ minWidth: 500 }}>
        <thead>
          <tr>
            <th>Type</th>
            <th>Date</th>
            <th>Asset/Vehicle</th>
            <th>Operator</th>
            <th>Doc #</th>
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => {
            const assetDisplay = getAssetDisplay(item);
            // Determine the "operator" based on collection type
            let operatorDisplay = item.driverOperator || item.driverNickName || "—";
            if (item._collection === "capturedFuelTankTransfer") {
              operatorDisplay = (item.fuelReceiverSign as string) || "—";
            } else if (item._collection === "capturedFuelTankIssue") {
              operatorDisplay = (item.driverNameAndSurname as string) || "—";
            } else if (item._collection === "capturedBulkFuelReceived") {
              operatorDisplay = (item.supplierDriverName as string) || "—";
            } else if (item._collection === "capturedTankLevelReading") {
              operatorDisplay = (item.signedBy as string) || "—";
            }

            return (
              <tr
                key={item.id + i}
                style={{ cursor: "pointer" }}
                onClick={() => navigate(FUEL_ROUTE_MAP[item._collection as keyof typeof FUEL_ROUTE_MAP] || "/dashboard/capturedRefuel")}
              >
                <td>
                  <Badge size="sm" color={FUEL_TYPE_COLORS[item._typeLabel as keyof typeof FUEL_TYPE_COLORS] || "gray"} variant="filled">
                    {item._typeLabel}
                  </Badge>
                </td>
                <td>{toDateString(item.transactionDate)}</td>
                <td>{assetDisplay}</td>
                <td>{operatorDisplay}</td>
                <td>{item.documentNumber || "—"}</td>
                <td>
                  <VerifiedIcon element={item} size={18} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </ScrollArea>
  );
}

export function FlexTable({ items, navigate }: { items: FlexOverviewItem[]; navigate: NavigateFunction }) {
  if (!items?.length) {
    return (
      <Text size="sm" color="dimmed" py="md" align="center">
        No recent form submissions
      </Text>
    );
  }
  return (
    <ScrollArea mt="xs">
      <Table striped highlightOnHover style={{ minWidth: 500 }}>
        <thead>
          <tr>
            <th>Template</th>
            <th>Date</th>
            <th>Submitted by</th>
            <th>Doc #</th>
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr
              key={item.id + i}
              style={{ cursor: "pointer" }}
              onClick={() => {
                const params = new URLSearchParams();
                if (item.templateId) params.set("templateId", String(item.templateId));
                if (item.id) params.set("submissionId", item.id);
                params.set("mode", "view");
                const dateStr = toIsoDateOnly(item.submissionDate);
                if (dateStr) {
                  params.set("from", dateStr);
                  params.set("to", dateStr);
                }
                navigate(`/dashboard/form-template-submissions?${params.toString()}`);
              }}
            >
              <td>
                <Badge size="sm" color="cap-blue" variant="light">
                  {item._templateName || "—"}
                </Badge>
              </td>
              <td>{toDateString(item.submissionDate)}</td>
              <td>{item.submitterName || item.submitterEmail || "—"}</td>
              <td>{item.documentNumber || "—"}</td>
              <td>
                <VerifiedIcon element={item} size={18} />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </ScrollArea>
  );
}

export function TripTable({ items, navigate }: { items: TripOverviewItem[]; navigate: NavigateFunction }) {
  if (!items?.length) {
    return (
      <Text size="sm" color="dimmed" py="md" align="center">
        No recent trips
      </Text>
    );
  }
  return (
    <ScrollArea mt="xs">
      <Table striped highlightOnHover style={{ minWidth: 500 }}>
        <thead>
          <tr>
            <th>Description</th>
            <th>Status</th>
            <th>Pickup</th>
            <th>Vehicle</th>
            <th>Driver</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr
              key={item.id + i}
              style={{ cursor: "pointer" }}
              onClick={() => navigate("/dashboard/trips")}
            >
              <td>{item.description || "—"}</td>
              <td>
                <Badge
                  size="sm"
                  variant="dot"
                  color={
                    item.status === "COMPLETED"
                      ? "green"
                      : item.status === "IN_PROGRESS"
                        ? "cap-orange"
                        : "cap-navy"
                  }
                >
                  {item.status || "—"}
                </Badge>
              </td>
              <td>{item.pickupAddress || "—"}</td>
              <td>{item.fleetDisplayName || "—"}</td>
              <td>{item.fleetOperatorNameAndSurname || "—"}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </ScrollArea>
  );
}

// Pin color config per source type
const PIN_COLORS = {
  fuel: "#E53935",   // red
  trip: "#1E88E5",   // blue
  flex: "#43A047",   // green
};

const PLAIN_MAP_STYLES = [
  {
    featureType: "all",
    elementType: "geometry",
    stylers: [{ color: "#f5f5f5" }],
  },
  {
    featureType: "all",
    elementType: "labels.text.fill",
    stylers: [{ color: "#9e9e9e" }],
  },
  {
    featureType: "all",
    elementType: "labels.text.stroke",
    stylers: [{ color: "#f5f5f5" }],
  },
  {
    featureType: "landscape",
    elementType: "geometry",
    stylers: [{ color: "#f5f5f5" }],
  },
  {
    featureType: "poi",
    elementType: "all",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#cccccc" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#adadad" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{ color: "#9e9e9e" }],
  },
  {
    featureType: "transit",
    elementType: "all",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#e9e9e9" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#9e9e9e" }],
  },
];

function getAssetDisplay(item: FuelOverviewItem): string {
  if (!item) return "—";
  if (item._collection === "capturedFuelTankTransfer") {
    return (item.mobileTankName as string) || "—";
  }
  if (item._collection === "capturedBulkFuelReceived" || item._collection === "capturedTankLevelReading") {
    return (item.tankName as string) || "—";
  }
  return (item.fleetDisplayName as string) || "—";
}

function getReceivingEntity(item: FuelOverviewItem): string {
  if (!item) return "—";
  // For fuel, "receiving entity" is what gets the fuel
  if (item._collection === "capturedFuelTankTransfer") {
    return (item.mobileTankName as string) || "—";
  }
  if (item._collection === "capturedBulkFuelReceived") {
    return (item.tankName as string) || "—";
  }
  if (item._collection === "capturedTankLevelReading") {
    return (item.tankName as string) || "—";
  }
  // Refuels and onsite issues go to vehicles
  return (item.fleetDisplayName as string) || "—";
}

function getFuelLabel(item: FuelOverviewItem): string {
  const collection = item._collection;
  if (collection === "capturedFuelRefilled") return "Refuel";
  if (collection === "capturedFuelTankTransfer") return "Tank Transfer";
  if (collection === "capturedBulkFuelReceived") return "Bulk Receipt";
  if (collection === "capturedTankLevelReading") return "Tank reading";
  if (collection === "capturedFuelIssued") return "Fuel Issue";
  return "Fuel";
}

interface TripEvent {
  label: string;
  date: DateInput;
  notes: string;
}

function getTripEvents(trip: TripOverviewItem): TripEvent[] {
  const events: TripEvent[] = [];
  const add = (arr: { submissionDate?: DateInput; date?: DateInput; capturedDate?: DateInput; notes?: string; delayReason?: string; reason?: string }[] | undefined, label: string) => {
    if (!arr?.length) return;
    arr.forEach(d => {
      events.push({
        label,
        date: d.submissionDate || d.date || d.capturedDate,
        notes: d.notes || d.delayReason || d.reason || "",
      });
    });
  };

  add(trip.capturedTripDepartData, "Departed");
  add(trip.capturedTripCheckPointData, "Checkpoint");
  add(trip.capturedTripDelayData, "Delay");
  add(trip.capturedTripArriveData, "Arrived");
  add(trip.capturedTripCompleteData, "Completed");

  return events.sort((a, b) => {
    const getTime = (v: DateInput) => {
      if (!v) return 0;
      if (typeof v === "object" && v !== null && ("seconds" in v || "_seconds" in v)) {
        return (v.seconds || v._seconds || 0) * 1000;
      }
      return new Date(v as string | number | Date).getTime();
    };
    return getTime(a.date) - getTime(b.date);
  });
}

const MAP_OPTIONS = {
  styles: PLAIN_MAP_STYLES,
  disableDefaultUI: false,
  mapTypeControl: true,
  streetViewControl: false,
  fullscreenControl: true,
};

interface MapMarker {
  position: { lat: number; lng: number };
  color: string;
  source: "Fuel" | "Trip" | "Flex";
  label?: string;
  detail?: string;
  item: any;
}

const GOOGLE_MAPS_LIBRARIES: ("places" | "drawing" | "geometry" | "visualization")[] = ["places"];

function OverviewMap({ fuel, flex, trips }: { fuel: FuelOverviewItem[]; flex: FlexOverviewItem[]; trips: TripOverviewItem[] }) {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries: GOOGLE_MAPS_LIBRARIES,
  });
  const [selectedPin, setSelectedPin] = useState<MapMarker | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);

  const markers = useMemo<MapMarker[]>(() => {
    const result: MapMarker[] = [];
    (fuel || []).forEach((item) => {
      const gps = extractGps(item);
      if (gps) {
        result.push({
          position: gps,
          color: PIN_COLORS.fuel,
          source: "Fuel",
          label: item._typeLabel || "Fuel",
          detail: `${getReceivingEntity(item)} • ${toDateString(item.transactionDate)}`,
          item,
        });
      }
    });
    (trips || []).forEach((item) => {
      const gps = extractGps(item);
      if (gps) {
        result.push({
          position: gps,
          color: PIN_COLORS.trip,
          source: "Trip",
          label: item.description || "Trip",
          detail: `${item.pickupAddress || item.fleetDisplayName || "Trip"} • ${item.status || "Unknown"}`,
          item,
        });
      }
    });
    (flex || []).forEach((item) => {
      const gps = extractGps(item);
      if (gps) {
        result.push({
          position: gps,
          color: PIN_COLORS.flex,
          source: "Flex",
          item,
        });
      }
    });
    return result;
  }, [fuel, flex, trips]);

  const lastFittedMarkersRef = useRef<MapMarker[] | null>(null);

  useEffect(() => {
    if (map && markers.length > 0 && window.google) {
      const activeMap = map;
      // Only fit bounds if markers have actually changed to avoid "jumping" on every click
      if (lastFittedMarkersRef.current === markers) return;
      lastFittedMarkersRef.current = markers;

      const bounds = new window.google.maps.LatLngBounds();
      markers.forEach((m) => bounds.extend(m.position));
      activeMap.fitBounds(bounds);
      
      // If only one marker, fitBounds might zoom in too much
      if (markers.length === 1) {
        const listener = window.google.maps.event.addListener(activeMap, 'idle', () => {
          const zoom = activeMap.getZoom();
          if (zoom !== undefined && zoom > 15) activeMap.setZoom(15);
          window.google.maps.event.removeListener(listener);
        });
      }
    }
  }, [map, markers]);

  const markerIcons = useMemo(() => {
    if (!isLoaded || !window.google) return [];
    return markers.map((m) => ({
      path: "M 12,2 C 8.13,2 5,5.13 5,9 c 0,5.25 7,13 7,13 0,0 7,-7.75 7,-13 0,-3.87 -3.13,-7 -7,-7 z",
      fillColor: m.color,
      fillOpacity: 1,
      strokeWeight: 2,
      strokeColor: "#FFF",
      scale: 1.4,
      anchor: new window.google.maps.Point(12, 22),
    }));
  }, [markers, isLoaded]);

  if (!isLoaded) {
    return <Loader color="cap-navy" style={{ margin: "2em auto", display: "block" }} />;
  }

  if (markers.length === 0) {
    return (
      <Alert color="yellow" title="No GPS data" style={{ marginTop: "0.5em" }}>
        None of the recent transactions have GPS coordinates attached.
      </Alert>
    );
  }

  return (
    <Paper shadow="xs" radius="md" mt="xs" style={{ overflow: "hidden" }}>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "450px" }}
        onLoad={(m) => {
          setMap(m);
        }}
        onUnmount={() => setMap(null)}
        options={MAP_OPTIONS}
      >
        {markers.map((m, i) => (
          <Marker
            key={`pin-${i}`}
            position={m.position}
            icon={markerIcons[i]}
            onClick={() => setSelectedPin(m)}
          />
        ))}
        {selectedPin && selectedPin.source === "Trip" && selectedPin.item.locationSnapshots?.length > 0 && (
          <Polyline
            path={selectedPin.item.locationSnapshots
              .map((snap: any) => {
                const lat = Number(snap.lat ?? snap.latitude ?? snap._latitude);
                const lng = Number(snap.lon ?? snap.lng ?? snap.longitude ?? snap._longitude);
                if (isNaN(lat) || isNaN(lng)) return null;
                return { lat, lng };
              })
              .filter((p: any) => p !== null)
            }
            options={{
              strokeColor: PIN_COLORS.trip,
              strokeOpacity: 0.8,
              strokeWeight: 4,
            }}
          />
        )}

        {selectedPin && (
          <InfoWindow
            position={selectedPin.position}
            onCloseClick={() => setSelectedPin(null)}
            options={{ 
              headerDisabled: true,
              pixelOffset: window.google ? new window.google.maps.Size(0, -35) : undefined
            }}
          >
            <div style={{ minWidth: 160 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <span
                  style={{
                    display: "inline-block",
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: selectedPin.color,
                  }}
                />
                <strong>{selectedPin.source}</strong>
              </div>
              {selectedPin.source === "Flex" ? (
                <>
                  <div style={{ fontSize: 13, fontWeight: 700, color: selectedPin.color }}>
                    {selectedPin.item._templateName || selectedPin.item.templateName || "Flex Submission"}
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "#444", marginTop: 4, fontWeight: 500 }}>
                    {selectedPin.item.submittedByName || selectedPin.item.submittedByEmail || "—"}
                  </div>
                  <div style={{ fontSize: "0.7rem", color: "#888" }}>
                    {toDateString(selectedPin.item.submissionDate)}
                  </div>
                </>
              ) : selectedPin.source === "Fuel" ? (
                <>
                  <div style={{ fontSize: 13, fontWeight: 700, color: selectedPin.color }}>
                    {getFuelLabel(selectedPin.item)}
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "#444", marginTop: 4, fontWeight: 600 }}>
                    {getReceivingEntity(selectedPin.item)}
                  </div>
                  <div style={{ fontSize: "0.7rem", color: "#888" }}>
                    {toDateString(selectedPin.item.transactionDate || selectedPin.item.submissionDate || selectedPin.item.date)}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 13, fontWeight: 700, color: selectedPin.color }}>
                    {selectedPin.label}
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "#444", marginTop: 4 }}>
                    {selectedPin.detail}
                  </div>
                </>
              )}
              {selectedPin.source === "Trip" && (
                <div style={{ marginTop: 8, borderTop: "1px solid #eee", paddingTop: 8, maxHeight: 150, overflowY: "auto" }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#444", marginBottom: 4 }}>Trip Timeline:</div>
                  {getTripEvents(selectedPin.item).map((ev, idx) => (
                    <div key={idx} style={{ marginBottom: 6, paddingLeft: 8, borderLeft: `2px solid ${ev.label === "Delay" ? "#f08c00" : "#228be6"}` }}>
                      <div style={{ fontSize: 11, fontWeight: 500 }}>
                        {ev.label} <span style={{ fontWeight: 400, color: "#888", fontSize: 10 }}>• {toDateString(ev.date)}</span>
                      </div>
                      {ev.notes && (
                        <div style={{ fontSize: 10, color: "#555", fontStyle: "italic", marginTop: 1 }}>
                          "{ev.notes}"
                        </div>
                      )}
                    </div>
                  ))}
                  {getTripEvents(selectedPin.item).length === 0 && (
                    <div style={{ fontSize: 10, color: "#999" }}>No stages recorded yet</div>
                  )}
                </div>
              )}
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
      <Group px="md" py="xs" spacing="lg">
        <Group spacing={4}>
          <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: PIN_COLORS.fuel }} />
          <Text size="xs" color="dimmed">Fuel</Text>
        </Group>
        <Group spacing={4}>
          <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: PIN_COLORS.trip }} />
          <Text size="xs" color="dimmed">Trip</Text>
        </Group>
        <Group spacing={4}>
          <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: PIN_COLORS.flex }} />
          <Text size="xs" color="dimmed">Flex</Text>
        </Group>
      </Group>
    </Paper>
  );
}

// --- Main Component ---

export default function CompanyOverview({ companyId }: { companyId: string }) {
  const navigate = useNavigate();
  const [data, setData] = useState<{
    fuel: FuelOverviewItem[];
    flex: FlexOverviewItem[];
    trips: TripOverviewItem[];
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mapExpanded, setMapExpanded] = useState(false);


  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;

    async function fetchOverview() {
      setLoading(true);
      setError(null);
      try {
        const token = await auth.currentUser?.getIdToken();
        if (!token) throw new Error("Not authenticated");

        const resp = await fetch(
          `${API_BASE}/overview?companyId=${encodeURIComponent(companyId)}`,
          {
            headers: {
              "x-api-key": X_API_KEY,
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!resp.ok) throw new Error(`Server error: ${resp.status}`);
        const json = await resp.json();
        if (!cancelled) setData(json);
      } catch (err) {
        console.error("Overview fetch failed:", err);
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchOverview();
    return () => { cancelled = true; };
  }, [companyId]);

  if (loading) {
    return <Loader color="cap-navy" style={{ margin: "4em auto", display: "block" }} />;
  }

  if (error) {
    return (
      <Alert color="red" title="Error loading overview" style={{ marginTop: "1em" }}>
        {error}
      </Alert>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div style={{ maxWidth: 1200 }}>
      <Paper
        shadow="xs"
        p="md"
        radius="md"
        mb="lg"
        style={{ border: "1px solid #eee", overflow: "hidden" }}
      >
        <SectionHeader
          icon={null}
          title="Captiva AI Assistant"
          actionLabel="Chat"
          onAction={() => navigate("/dashboard/ai-assistant")}
        />
      </Paper>

      <Paper
        shadow="xs"
        p="md"
        radius="md"
        mb="lg"
        style={{ border: "1px solid #eee" }}
      >
        <SectionHeader
          icon={<MapSearch size={22} color="#6331ff" />}
          title="Recent Activity Map"
          actionLabel={mapExpanded ? "Hide map" : "Show map"}
          onAction={() => setMapExpanded((v) => !v)}
        />
        {mapExpanded && (
          <OverviewMap
            fuel={data.fuel}
            flex={data.flex}
            trips={data.trips}
          />
        )}
      </Paper>

      {/* Fuel Section */}
      <Paper
        shadow="xs"
        p="md"
        radius="md"
        mb="lg"
        style={{ border: "1px solid #eee" }}
      >
        <SectionHeader
          icon={<GasStation size={22} color="#E53935" />}
          title="Captiva Fuel — Recent Transactions"
          actionLabel="View all"
          onAction={() => navigate("/dashboard/capturedRefuel")}
        />
        <FuelTable items={data.fuel} navigate={navigate} />
      </Paper>

      {/* Flex Section */}
      <Paper
        shadow="xs"
        p="md"
        radius="md"
        mb="lg"
        style={{ border: "1px solid #eee" }}
      >
        <SectionHeader
          icon={<ClipboardData size={22} color="#6482fc" />}
          title="Captiva Flex — Recent Submissions"
          actionLabel="View all"
          onAction={() => navigate("/dashboard/form-template-submissions")}
        />
        <FlexTable items={data.flex} navigate={navigate} />
      </Paper>

      {/* Trips Section */}
      <Paper
        shadow="xs"
        p="md"
        radius="md"
        mb="lg"
        style={{ border: "1px solid #eee" }}
      >
        <SectionHeader
          icon={<Route size={22} color="#1E88E5" />}
          title="Captiva Trip — Recent Trips"
          actionLabel="View all"
          onAction={() => navigate("/dashboard/trips")}
        />
        <TripTable items={data.trips} navigate={navigate} />
      </Paper>
    </div>
  );
}
